import {useState} from 'react'
import {useTranslation} from 'react-i18next'
import {useNavigate} from 'react-router-dom'
import {ArrowLeftGlyph} from '@/components/ui/icons'
import {ConfirmDialog} from '@/components/confirm-dialog'
import {EmployeeViewAsCombobox} from '@/components/employee-view-as-combobox'
import {useAllEmployees} from '@/hooks/empresa/identidad/use-employees'
import {useGodMode} from '@/hooks/platform/use-god-mode'
import {useViewAs} from '@/hooks/platform/use-view-as'
import {cn} from '@/lib/utils'

/** Armar la edición pide confirmación porque la API ya concede todo al modo dios; desarmar no. */
function GodModeSwitch({businessName}: {businessName: string | undefined}) {
  const {t} = useTranslation()
  const {armed, setArmed} = useGodMode()
  const [confirming, setConfirming] = useState(false)
  return (
    <div className="mt-4 space-y-1.5 rounded-lg bg-white/5 p-2.5" data-testid="god-mode">
      <span
        className={cn(
          'rounded-full px-2 py-0.5 font-mono text-[10px] font-semibold tracking-wider uppercase',
          armed ? 'tone-red' : 'tone-amber'
        )}>
        {t(armed ? 'godmode.editing' : 'godmode.readOnly')}
      </span>
      <p className="text-[11px] leading-snug text-white/50">{t('godmode.hint')}</p>
      <button
        type="button"
        data-testid="god-mode-toggle"
        onClick={() => (armed ? setArmed(false) : setConfirming(true))}
        className="w-full cursor-pointer rounded-md border border-white/20 px-2 py-1 text-xs font-semibold text-white/80 hover:bg-white/10 hover:text-white">
        {t(armed ? 'godmode.disable' : 'godmode.enable')}
      </button>
      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        title={t('godmode.confirmTitle')}
        description={t('godmode.confirmDescription', {name: businessName ?? ''})}
        confirmLabel={t('godmode.confirmAction')}
        cancelLabel={t('common.cancel')}
        onConfirm={() => {
          setArmed(true)
          setConfirming(false)
        }}
      />
    </div>
  )
}

export function GodModeContentChip({businessName}: {businessName: string}) {
  const {t} = useTranslation()
  const {armed} = useGodMode()
  return (
    <div className="flex items-center gap-2" data-testid="god-mode-content-chip">
      <span className="font-mono text-[11px] tracking-wide text-muted-foreground uppercase">{businessName}</span>
      <span
        className={cn(
          'rounded-full px-2 py-0.5 font-mono text-[10px] font-semibold tracking-wider uppercase',
          armed ? 'tone-red' : 'tone-amber'
        )}>
        {t(armed ? 'godmode.editing' : 'godmode.readOnly')}
      </span>
    </div>
  )
}

/** Elegir a alguien abre una observación de solo lectura de la app tal como esa persona la ve. */
function ViewAsSelect({businessId}: {businessId: string}) {
  const {t} = useTranslation()
  const {target, viewAs} = useViewAs()
  const {data: employees} = useAllEmployees(businessId)
  return (
    <div className="mt-3 space-y-1.5 rounded-lg bg-white/5 p-2.5" data-testid="view-as">
      <span className="block font-mono text-[10px] font-semibold tracking-wider text-white/60 uppercase">
        {t('viewAs.label')}
      </span>
      {!target && <p className="text-[11px] leading-snug text-white/50">{t('viewAs.hint')}</p>}
      <EmployeeViewAsCombobox
        roster={employees ?? []}
        value={target}
        onChange={(emp) => viewAs({id: emp.id, name: `${emp.name} ${emp.last_name}`.trim()})}
        testId="view-as-select"
      />
    </div>
  )
}

/** La única salida: mientras se opera un tenant, la navegación de la consola admin desaparece. */
export function GodModeExit({businessId}: {businessId: string}) {
  const {t} = useTranslation()
  const navigate = useNavigate()
  return (
    <button
      type="button"
      data-testid="god-mode-exit"
      onClick={() => navigate(`/admin/empresas/${businessId}`)}
      className="mb-1 flex w-full cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm font-medium text-white/70 hover:bg-white/10 hover:text-white">
      <span aria-hidden="true">
        <ArrowLeftGlyph />
      </span>
      {t('godmode.exit')}
    </button>
  )
}

/** Con una lente activa no hay interruptor de edición: solo tiene sentido operando como dueño. */
export function GodModeSidebarControls({businessId, businessName}: {businessId: string; businessName: string}) {
  const {target} = useViewAs()
  return (
    <>
      {!target && <GodModeSwitch businessName={businessName} />}
      <ViewAsSelect businessId={businessId} />
    </>
  )
}

/** Es seguridad: está en toda página y nunca se oculta sola. */
export function ViewAsBanner() {
  const {t} = useTranslation()
  const {target, exit} = useViewAs()
  if (!target) return null
  return (
    <div
      data-testid="view-as-banner"
      className="flex min-w-0 items-center gap-2 rounded-full border border-primary/30 bg-accent px-2.5 py-1">
      <span aria-hidden="true" className="h-2 w-2 shrink-0 rounded-full bg-primary" />
      <span className="min-w-0 truncate text-xs font-medium text-primary">
        {t('viewAs.banner', {name: target.name})}
      </span>
      <button
        type="button"
        data-testid="view-as-exit"
        onClick={exit}
        className="shrink-0 cursor-pointer rounded-md border border-primary/40 px-2 py-0.5 text-[11px] font-semibold text-primary hover:bg-primary/10">
        {t('viewAs.exit')}
      </button>
    </div>
  )
}
